import { PROTOCOL_TEST_IDS } from '@/components/admin/protocoles/contracts';
import { CriterionCardPopover } from '@/components/admin/protocoles/step/criterion-card-popover';
import { CriterionThresholdSlider } from '@/components/admin/protocoles/step/criterion-threshold-slider';
import { ReferencePill, ReferenceSignal } from '@/components/admin/versioned/reference-control';
import { useAdminSession } from '@/lib/admin-session';
import { fetchCriterionVersion } from '@/lib/api';
import { pinWord } from '@/lib/references';
import { useReferenceVersions } from '@/lib/use-reference-versions';
import { versionedDay } from '@/lib/versioned-labels';
import type {
  CriterionCard,
  CriterionRequirement,
  CriterionTagScoring,
  VersionPin,
} from '@matter_extractor/shared';
import { plancherBound } from '@matter_extractor/shared';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, TriangleAlert, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useCallback, useState } from 'react';

/**
 * One criterion of an objective's « condition de validation » (refonte
 * modulaire §7.2; maquette `.crit-row`, `thresholdHTML`; vitrine.md §3.1,
 * planche `references.html` section 1).
 *
 * One fluid line rather than a table: the name, the version played, the
 * optional threshold, and the cross in the gutter. All the criteria of an
 * objective are REQUIRED together (§7.2), which is why the line carries no
 * « et » and no operator between rows - there is nothing to choose there.
 *
 * ## The threshold belongs to the criterion's NOTATION, and the row says so
 *
 * Three states, and which one a row is in is a fact about the version its pin
 * resolves to (§7.2, grill of 2026-09-16):
 *
 * - the version is **scored and no threshold is posed**: a quiet pill « seuil :
 *   plancher 50 » and a « + seuil » button, which unfolds the slider of
 *   `docs/specs/maquettes/notation/Objectif.dc.html` under the row;
 * - the version is **scored and a threshold is posed**: the pill reads « seuil
 *   75 » in bleu nuit and toggles the same panel;
 * - the version has **no notation**: nothing at all, « pas même verdict » -
 *   there is no bar to pose and naming its absence would be an answer to a
 *   question nobody asked. Unless a threshold is posed there anyway, which is
 *   the garde au gel of H47: a yellow pill says so, a cross IN it removes the
 *   threshold, « + seuil » is not offered, and `threshold-without-scoring`
 *   refuses the passage to production.
 *
 * The three states are asked in that order, and only of a citation that
 * RESOLVES: an unknown criterion and a retired one each carry their own word on
 * the row, and a threshold beside one of them is a consequence rather than a
 * second thing to fix. The row reads that the way `productionBlockers` does, so
 * the editor and the refusal at « Passer en production » can never name
 * different faults for one citation.
 *
 * ## Which version a citation resolves to
 *
 * `latest` is the development version - « latest dev » - and a number is that
 * very version. The card the library hands the row knows the notation of the two
 * versions the pointers sit on; a citation pinned to ANY OTHER version is read
 * from the version itself (`UnpointedCitation`), a component that exists so the
 * queries it needs are mounted only for those citations - the row of a `latest`
 * stays a pure render of its card.
 *
 * ## Two absences that are not the same absence
 *
 * A criterion cited but not in the library the page handed us is UNKNOWN while
 * the library has not been read, and MISSING once it has. The régime is the
 * persona form's `libraryRead`, and for the same reason: an empty list is
 * ambiguous - a read in flight, a read that failed, a library that really lost
 * it - and « critère introuvable » ACCUSES. So the row shows the bare id in
 * grey and says nothing until `criteriaRead` is true.
 *
 * A RETIRED criterion is a third thing again: it exists, it still names itself,
 * and it is a refusal waiting at « Passer en production » (§4). It keeps its
 * name and carries the day it went, in yellow.
 *
 * ## The version pill is a MENU, and a pinned one can say it is behind
 *
 * « latest · v6 sur le dev », or « v4 »: `ReferencePill`, whose menu lists
 * `latest` first and every version after it. A pinned citation behind the latest
 * dev version carries the signal under the row - « Sens profond v6 disponible -
 * ce protocole épingle v4 » - and « Mettre à jour » moves the pin, in the draft.
 *
 * ## The fiche is `fixed`, and it closes (issue #104, D3 and D7)
 *
 * The fiche was `absolute top-full left-0`, which anchors it in the row's own
 * positioning context and therefore inside every `overflow` above it: the step
 * editor scrolls, and the last rows of an objective had their panel cropped by
 * the bottom of it. It stands in `position: fixed`, placed on the rectangle of
 * the control that opens it by `useFloatingPanel`, which also gives it the
 * closing it never had - Escape with the focus handed back, and a click outside.
 * It is not portalled, and the row keeps it as its own child: a `fixed` element
 * escapes clipping without moving in the tree, and the fiche is also mounted
 * inside `variable-fiche.tsx`, whose outside-click test is a `contains` on its
 * own box.
 */

export interface CriterionRequirementRowProps {
  requirement: CriterionRequirement;
  /** The library's card for this criterion, or null when it holds none. */
  card: CriterionCard | null;
  /** False while the library has not been read: an absence is not yet a defect. */
  criteriaRead: boolean;
  readOnly: boolean;
  /**
   * True inside the frame of a « l'un de » group (issue #181): the rail on the
   * row's left takes the frame's tint instead of the grey of a lone line.
   */
  grouped?: boolean;
  onChange: (next: CriterionRequirement) => void;
  onRemove: () => void;
}

const PILL_SHAPE =
  'inline-flex items-center gap-1.5 rounded-pill px-2.5 py-0.5 font-semibold text-xs leading-5';

/** The version a citation resolves to, and what it says about its notation (§7.2). */
type Cited = {
  /** The version, or null while it is read or when the object does not hold it. */
  readonly pointed: { readonly id: string; readonly number: number } | null;
  /** Its notation, null when it poses none - and meaningful only when `known`. */
  readonly scoring: CriterionTagScoring | null;
  /** False while the version is still being read: no claim is made before. */
  readonly known: boolean;
  /** True once the object was read and holds no such version. */
  readonly missing: boolean;
};

/**
 * What the CARD says of the version a pin resolves to - `latest` and the two
 * pointed versions - or `undefined` when only the version itself can say.
 */
function citedFromCard(card: CriterionCard, pin: VersionPin): Cited | undefined {
  if (pin === 'latest' || pin === card.development.number) {
    return {
      pointed: { id: card.development.id, number: card.development.number },
      scoring: card.scoring.development,
      known: true,
      missing: false,
    };
  }
  if (card.production !== null && pin === card.production.number) {
    return {
      pointed: { id: card.production.id, number: card.production.number },
      scoring: card.scoring.production,
      known: true,
      missing: false,
    };
  }
  return undefined;
}

export function CriterionRequirementRow(props: CriterionRequirementRowProps) {
  const { card, requirement } = props;
  const direct = card === null ? undefined : citedFromCard(card, requirement.ref.pin);
  if (card === null || direct !== undefined) {
    return (
      <RequirementLine
        {...props}
        cited={direct ?? { pointed: null, scoring: null, known: false, missing: false }}
      />
    );
  }
  return (
    <UnpointedCitation card={card} pin={requirement.ref.pin}>
      {(cited) => <RequirementLine {...props} cited={cited} />}
    </UnpointedCitation>
  );
}

/**
 * The version of a criterion that NO pointer sits on, read from the object: its
 * detail for the id of the version a number names, then the version for its
 * notation. Mounted only for such a citation, so the reads - and the session
 * and the query client they need - belong to the rare row and not to every one.
 */
function UnpointedCitation({
  card,
  pin,
  children,
}: {
  card: CriterionCard;
  pin: VersionPin;
  children: (cited: Cited) => ReactNode;
}) {
  const { email } = useAdminSession();
  const read = useReferenceVersions('criterion', card.id, true);
  const summary =
    read === null ? null : (read.versions.find((version) => version.number === pin) ?? null);
  const version = useQuery({
    queryKey: ['criterion-version', email, card.id, summary?.id ?? null],
    queryFn: () => fetchCriterionVersion(card.id, summary?.id ?? ''),
    enabled: summary !== null,
    retry: false,
  });
  const content = version.data?.content;
  const scoring: CriterionTagScoring | null =
    content === undefined || content.scoring === null
      ? null
      : { bandCount: content.scoring.bands.length, plancher: plancherBound(content.scoring) };

  return children({
    pointed: summary === null ? null : { id: summary.id, number: summary.number },
    scoring,
    known: content !== undefined,
    missing: read !== null && summary === null,
  });
}

function RequirementLine({
  requirement,
  card,
  criteriaRead,
  readOnly,
  grouped = false,
  cited,
  onChange,
  onRemove,
}: CriterionRequirementRowProps & { cited: Cited }) {
  const [popover, setPopover] = useState<'none' | 'card'>('none');
  const [panelOpen, setPanelOpen] = useState(false);
  /** The pin the last « Mettre à jour » moved from, said once under the row. */
  const [movedFrom, setMovedFrom] = useState<number | null>(null);
  /*
    The name as an ELEMENT and not as a ref: a panel placed on a rectangle has to
    re-render when the element it measures arrives, and a `useRef` would still be
    null on the render that mounts the panel. `useCallback` on the closing, so
    the panel's document listeners are hung once rather than on every render of
    the row.
  */
  const [nameEl, setNameEl] = useState<HTMLElement | null>(null);
  const closePopover = useCallback(() => setPopover('none'), []);

  const { threshold } = requirement;
  const pin = requirement.ref.pin;
  const { pointed, scoring } = cited;
  /*
    Does this citation RESOLVE to a version at all - the `resolves` of
    `productionBlockers`, read off the card instead of the base. A criterion the
    library has not handed us yet, one it has lost and a retired one each have
    their own word on the row already, and each is one gesture to repair.

    H47 is the question asked AFTER that one: the citation resolves, and the
    version it resolves to poses no scale. The engine will ignore the threshold,
    so the row warns rather than drawing a slider with no rail. Asked before,
    the yellow pill flashed on every thresholded row while the library was in
    flight. And it is asked only once the version is KNOWN: a pinned version still
    being read has no notation yet, which is not the same as having none.
  */
  const resolves = card !== null && card.retiredAt === null && !cited.missing;
  const orphanThreshold = threshold !== null && resolves && cited.known && scoring === null;
  const subject =
    card === null
      ? null
      : {
          kind: 'criterion' as const,
          id: card.id,
          name: card.name,
          who: 'ce protocole',
          pin,
          developmentNumber: card.development.number,
        };
  const writePin = (next: VersionPin) =>
    onChange({ ...requirement, ref: { id: requirement.ref.id, pin: next } });

  return (
    <div
      className={`flex flex-col border-l-2 pl-3 ${grouped ? 'border-bleu-nuit-tint' : 'border-gray-200'}`}
    >
      <div
        data-testid={PROTOCOL_TEST_IDS.criterionRow}
        data-criterion-id={requirement.ref.id}
        className="flex min-h-10 flex-wrap items-center gap-2.5 py-1 text-sm"
      >
        {card === null ? (
          <span className="font-semibold text-gray-500 text-sm">{requirement.ref.id}</span>
        ) : (
          <button
            ref={setNameEl}
            type="button"
            data-testid={PROTOCOL_TEST_IDS.criterionName}
            aria-haspopup="dialog"
            aria-expanded={popover === 'card'}
            onClick={() => setPopover((was) => (was === 'card' ? 'none' : 'card'))}
            className="inline-flex cursor-pointer items-center gap-1.5 font-semibold text-base text-bleu-nuit hover:underline hover:underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {card.name}
            <ChevronDown aria-hidden="true" className="size-3" />
          </button>
        )}

        {/*
          Right after the name it opens, and not at the end of the row: the
          panel is `fixed`, so its place in the tree costs no layout and buys
          the tab order a reader expects - the name, then what the name opened,
          then the rest of the line.
        */}
        {/*
          L'ANCRE est une condition de montage, pas une prop qui peut valoir
          `null` - correction de revue. Un `anchor` absent fait basculer la
          fiche dans son second régime, celui où son appelant l'a déjà placée
          (la fiche d'une pastille de variable) : ni Échap, ni clic dehors, et
          dans le flux de la ligne. Si la bibliothèque perd le critère pendant
          que la fiche est ouverte, le bouton démonte, `nameEl` passe à `null`,
          et on se retrouvait avec un panneau qu'on ne pouvait plus fermer.
        */}
        {popover === 'card' && nameEl !== null ? (
          <CriterionCardPopover
            criterionId={requirement.ref.id}
            anchor={nameEl}
            onClose={closePopover}
          />
        ) : null}

        {card === null && criteriaRead ? (
          <span className="inline-flex items-center gap-1.5 text-jaune-deep text-xs">
            <TriangleAlert aria-hidden="true" className="size-3.5" />
            critère introuvable
          </span>
        ) : null}

        {card !== null && card.retiredAt !== null ? (
          <span className="inline-flex items-center gap-1.5 text-jaune-deep text-xs">
            <TriangleAlert aria-hidden="true" className="size-3.5" />
            retiré le {versionedDay(card.retiredAt)}
          </span>
        ) : null}

        <span aria-hidden="true" className="text-gray-300">
          ·
        </span>

        {subject === null ? (
          <span className={`${PILL_SHAPE} bg-gray-100 text-gray-600`}>{pinWord(pin)}</span>
        ) : (
          <ReferencePill
            subject={subject}
            readOnly={readOnly}
            menuTestId={PROTOCOL_TEST_IDS.criterionVersion}
            testId="protocole-critere"
            onPin={(next) => {
              setMovedFrom(null);
              writePin(next);
            }}
          />
        )}

        {cited.missing ? (
          <span className="inline-flex items-center gap-1.5 text-jaune-deep text-xs">
            <TriangleAlert aria-hidden="true" className="size-3.5" />
            version introuvable
          </span>
        ) : null}

        {orphanThreshold || (cited.known && scoring !== null) ? (
          <span aria-hidden="true" className="text-gray-300">
            ·
          </span>
        ) : null}

        {/*
        H47 seen from the editor, and it wins over everything below: the version
        this citation points at poses no scale, so there is no rail to draw and
        no plancher to fall back on. The engine will ignore the threshold, and
        « Passer en production » refuses it - so the row says both facts in one
        yellow pill and offers no way to add another.

        The CROSS is the other half of it. The warning names two gestures,
        « retirer le seuil, ou noter le critère », and the second one is a trip
        to the criterion's own editor; the first one has to be doable here, or
        the only way out of a refusal at « Passer en production » is to go and
        score a criterion the operator may not want scored. There is no panel to
        carry the button - the panel is what a scale is for - so the pill does.

        INSIDE the pill and not beside it: the row is one wrapping line whose
        last item is the criterion's own cross, pushed right by `ml-auto`, and a
        second box between the two pills sent that one onto a line of its own -
        two crosses stacked, neither of them saying which it removed.
      */}
        {orphanThreshold ? (
          <span
            data-testid={PROTOCOL_TEST_IDS.criterionThresholdOrphan}
            className={`${PILL_SHAPE} bg-jaune-tint text-jaune-deep ${readOnly ? '' : 'pr-1'}`}
          >
            <TriangleAlert aria-hidden="true" className="size-3" />
            seuil {threshold} sans notation dans la {pinWord(pin)}
            {pin === 'latest' && pointed !== null ? ` (v${pointed.number})` : ''}
            {readOnly ? null : (
              <button
                type="button"
                data-testid={PROTOCOL_TEST_IDS.criterionThresholdRemove}
                aria-label="Retirer le seuil"
                onClick={() => onChange({ ...requirement, threshold: null })}
                className="-mr-0.5 inline-flex size-4 cursor-pointer items-center justify-center rounded-sm hover:bg-jaune-deep/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X aria-hidden="true" className="size-3" />
              </button>
            )}
          </span>
        ) : scoring === null || !cited.known ? null : (
          <>
            <button
              type="button"
              data-testid={PROTOCOL_TEST_IDS.criterionThreshold}
              aria-expanded={panelOpen}
              disabled={readOnly}
              onClick={() => setPanelOpen((was) => !was)}
              className={`${PILL_SHAPE} cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default ${
                threshold === null
                  ? 'bg-gray-100 text-gray-600'
                  : 'bg-bleu-nuit-tint text-bleu-nuit'
              }`}
            >
              {threshold === null ? `seuil : plancher ${scoring.plancher}` : `seuil ${threshold}`}
              {readOnly ? null : <ChevronDown aria-hidden="true" className="size-3" />}
            </button>
            {readOnly || threshold !== null || panelOpen ? null : (
              <button
                type="button"
                data-testid={PROTOCOL_TEST_IDS.criterionThresholdAdd}
                onClick={() => setPanelOpen(true)}
                className="cursor-pointer text-bleu-nuit text-caption underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                + seuil
              </button>
            )}
          </>
        )}

        {readOnly ? null : (
          <span className="ml-auto flex w-9 justify-end">
            <button
              type="button"
              data-testid={PROTOCOL_TEST_IDS.criterionRemove}
              aria-label="Retirer ce critère"
              onClick={onRemove}
              className="inline-flex size-8 cursor-pointer items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-rouge-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X aria-hidden="true" className="size-4" />
            </button>
          </span>
        )}
      </div>

      {/* The signal of a pinned citation that is behind (§3.3), under the line. */}
      {subject === null ? null : (
        <div className="mb-1 pl-3.5 empty:hidden">
          <ReferenceSignal
            subject={subject}
            readOnly={readOnly}
            movedFrom={movedFrom}
            testId="protocole-critere"
            onUpdate={(to) => {
              if (typeof pin === 'number') setMovedFrom(pin);
              writePin(to);
            }}
          />
        </div>
      )}

      {/*
        UNDER the row and inside the open objective, never in a popover (§7.2):
        the rail is as wide as the objective and carries a tick per band. It is
        mounted only while open, which is what keeps the scale's read lazy - ten
        cited criteria would otherwise be ten requests at mount.
      */}
      {panelOpen && !readOnly && card !== null && scoring !== null && pointed !== null ? (
        <CriterionThresholdSlider
          criterionId={requirement.ref.id}
          versionId={pointed.id}
          criterionName={card.name}
          threshold={threshold}
          onThreshold={(next) => onChange({ ...requirement, threshold: next })}
        />
      ) : null}
    </div>
  );
}
